// ---------------------------------------------------------------------------
// Helpers de EXIBIÇÃO compartilhados entre módulos.
//
// Só formatação para a UI — a validação de dígito-verificador fica em
// utils/validateCpfCnpj.js (reusada pelo InputCpfCnpj). O backend devolve os
// documentos só com dígitos; estas funções aplicam máscara apenas na exibição.
//
// Promovido de pages/Comercial/utils/format.js em 2026-08-04, quando o
// Financeiro (contas bancárias, handoff 0079) passou a precisar da mesma
// máscara de CPF/CNPJ — migrate-on-touch: extrair em vez de copiar.
//
// formatBRL promovido de pages/Finance/utils/dunningFormat.js em 2026-09-02,
// quando o billing (handoff 0105) passou a precisar de moeda null-safe.
// ---------------------------------------------------------------------------

export function onlyDigits(value) {
   return String(value ?? '').replace(/\D/g, '');
}

// Rótulo de usuário vindo de `GET /users/directory` (handoff 0110 §3.9). O
// diretório devolve ex-usuários quando `status=all`, porque filtro histórico
// não pode perder quem saiu da empresa — e aí o operador precisa enxergar que
// aquela conta não existe mais. Compartilhado porque três módulos renderizam a
// mesma lista: Movimentos (filtro), Recrutamento (entrevistador) e Onboarding
// (responsável do RH).
//
// O diretório não traz e-mail nem papel — e não deve trazer (ver o guardrail em
// services/users/usersApi.js). Os fallbacks cobrem só o caso de a lista vir de
// outra fonte.
export function formatUserLabel(user) {
   if (!user) return '—';
   const name = user.nome ?? user.name ?? `Usuário ${user.id}`;
   return user.is_active === false ? `${name} (inativo)` : name;
}

// Moeda null-safe. `null`/`undefined` = "não informado" → "—"; `0` é valor
// legítimo → "R$ 0,00". Nunca usar Number(value || 0) para campos que
// distinguem null de zero (ex.: amount_received do billing).
export function formatBRL(value) {
   if (value === null || value === undefined) return '—';
   return `R$ ${Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
   })}`;
}

// CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00) conforme a quantidade de
// dígitos. Valores fora do padrão são devolvidos como vieram (sem quebrar a UI).
export function formatDocForDisplay(doc) {
   const digits = onlyDigits(doc);
   if (!digits) return '—';

   if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
   }
   if (digits.length === 14) {
      return digits.replace(
         /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
         '$1.$2.$3/$4-$5',
      );
   }
   return doc;
}
